import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Check, CreditCard, AlertCircle, Loader2 } from 'lucide-react';

const CheckoutModal = ({ isOpen, onClose, planId, userEmail, userId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const { user, isAuthenticated } = useAuth();

  console.log('CheckoutModal props:', { isOpen, planId, userEmail, userId });
  console.log('CheckoutModal user from auth:', user);
  console.log('CheckoutModal isAuthenticated:', isAuthenticated);

  // Add effect to log when modal opens/closes
  useEffect(() => {
    console.log('CheckoutModal isOpen changed to:', isOpen);
    if (isOpen) {
      console.log('CheckoutModal is now OPEN');
    } else {
      console.log('CheckoutModal is now CLOSED');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && planId) {
      fetchPlanDetails();
    }
  }, [isOpen, planId]);

  const fetchPlanDetails = async () => {
    try {
      console.log('Fetching plan details for planId:', planId);
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      const response = await fetch(`${apiBase}/api/subscription/plans`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      
      const data = await response.json();
      console.log('Fetched plans data:', data);
      const selectedPlan = data.plans.find(p => p.plan_id === planId);
      console.log('Selected plan:', selectedPlan);
      
      if (!selectedPlan) {
        throw new Error('Plan not found');
      }
      
      setPlan(selectedPlan);
      console.log('Plan set successfully:', selectedPlan);
    } catch (err) {
      console.error('Error fetching plan details:', err);
      setError(err.message);
    }
  };

  const handlePayment = async () => {
    const email = userEmail || user?.email;
    const uid = userId || (user?.uid ? `user_${user.uid}` : null);
    if (!plan || !email || !uid) {
      setError('Please sign in to continue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      console.log('Creating subscription with:', plan);
      
      const response = await fetch(`${apiBase}/api/subscription/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: plan.plan_id,
          userEmail: email,
          userId: uid,
          product_id: plan.product_id || plan.dodo_product_id || undefined
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create subscription');
      }

      const data = await response.json();
      
      if (data.paymentUrl) {
        // Redirect to DODO payment page
        window.open(data.paymentUrl, '_blank');
        onClose();
      } else {
        throw new Error('No payment URL received');
      }
      
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return 'Unlimited';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Math.round(gb)}GB`;
  };

  const formatFileSize = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Math.round(gb)}GB`;
  };

  if (!isOpen) return null;

  // Show loading state while plan is being fetched
  if (!plan) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', width: '100%', maxWidth: '500px' }}>
          <div className="text-center">
            <Loader2 className="animate-spin mx-auto mb-4" style={{ height: '32px', width: '32px', color: '#16a34a' }} />
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '8px' }}>Loading...</h2>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>
              Preparing your subscription details
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', width: '100%', maxWidth: '500px' }}>
        <div className="text-center mb-6">
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '8px' }}>Complete Your Purchase</h2>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            Upgrade to {plan?.plan_name} plan for enhanced features
          </p>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Plan Summary */}
          <div style={{ backgroundColor: '#374151', borderRadius: '2px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontWeight: '600', color: 'white' }}>{plan?.plan_name} Plan</h3>
              <div style={{ backgroundColor: '#3b82f6', color: 'white', padding: '4px 12px', borderRadius: '2px', fontSize: '12px', fontWeight: '600' }}>
                ${plan?.price_usd}/month
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981' }} />
                <span style={{ color: '#d1d5db' }}>{formatFileSize(plan?.max_file_size_bytes)} max file size</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981' }} />
                <span style={{ color: '#d1d5db' }}>{formatBytes(plan?.storage_quota_bytes)} storage quota</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981' }} />
                <span style={{ color: '#d1d5db' }}>{plan?.expiry_days} days expiry</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981' }} />
                <span style={{ color: '#d1d5db' }}>Unlimited downloads</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981' }} />
                <span style={{ color: '#d1d5db' }}>Resumable uploads</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#9ca3af' }}>
              <CreditCard style={{ height: '16px', width: '16px' }} />
              <span>You will be redirected to DODO Payments for secure checkout</span>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>${plan?.price_usd}</div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>per month</div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={{ backgroundColor: '#dc2626', color: 'white', padding: '10px', borderRadius: '2px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle style={{ height: '16px', width: '16px' }} />
              {error}
            </div>
          )}

          {/* Payment Button */}
          <div>
            <button 
              onClick={handlePayment}
              disabled={loading || !isAuthenticated}
              style={{ 
                backgroundColor: '#3b82f6', 
                color: 'white', 
                padding: '10px 16px', 
                borderRadius: '3px',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                width: '100%',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <Loader2 style={{ height: '20px', width: '20px', animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard style={{ height: '20px', width: '20px' }} />
                  Pay ${plan?.price_usd}/month
                </>
              )}
            </button>
          </div>

          {/* Security Notice */}
          <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
            Your payment is processed securely by DODO Payments. 
            You can cancel your subscription anytime from your profile.
          </div>

          {/* Close Button */}
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={onClose}
              style={{ 
                backgroundColor: 'transparent', 
                color: '#9ca3af', 
                padding: '8px 12px', 
                borderRadius: '3px',
                border: '1px solid #4b5563',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;